import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Check,
  CircleHelp,
  Loader2,
  Lock,
  MessagesSquare,
  Pin,
  Plus,
  Search,
  Tags,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ForumThread } from "@/components/ForumThread";
import { KindBadge, VoteArrows, ago } from "@/components/ForumComment";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SORTS = [
  ["new", "New"],
  ["top", "Top"],
  ["discussed", "Most discussed"],
  ["active", "Active"],
];

const Composer = ({ tags, onDone, onCancel }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("question");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (tag) =>
    setPicked((p) =>
      p.includes(tag) ? p.filter((t) => t !== tag) : p.length >= 3 ? p : [...p, tag],
    );

  const submit = () => {
    setBusy(true);
    api
      .post("/forum", { title, body, tags: picked, kind })
      .then((r) => {
        toast.success("Your thread is on the forum.");
        onDone(r.data?.thread?.id);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="border border-primary bg-card p-5" data-testid="forum-composer">
      <div className="flex items-center justify-between">
        <div className="label-caps text-primary">Start a thread</div>
        <button type="button" data-testid="forum-composer-close" onClick={onCancel}>
          <X className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
        </button>
      </div>

      <div className="mt-4 flex gap-px border border-border bg-border">
        {[
          ["question", "A question", CircleHelp],
          ["discussion", "A discussion", MessagesSquare],
        ].map(([value, label, Icon]) => (
          <button
            key={value}
            type="button"
            data-testid={`forum-kind-${value}`}
            onClick={() => setKind(value)}
            className={`flex flex-1 items-center justify-center gap-2 px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
              kind === value
                ? "bg-foreground text-background"
                : "bg-card text-muted-foreground hover:bg-muted"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      <Input
        data-testid="forum-title-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={
          kind === "question"
            ? "What do you need to know? e.g. How did BSL baseline its slag rate?"
            : "What do you want the units to discuss?"
        }
        className="mt-4 h-11 rounded-none bg-background"
      />
      <textarea
        data-testid="forum-body-input"
        rows={5}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Say what you have already tried, and what you are stuck on. The more you put here, the better the answers."
        className="mt-3 w-full resize-y border border-border bg-background px-3 py-2 text-sm outline-none transition-colors duration-200 focus:border-primary"
      />

      <div className="mt-4">
        <div className="label-caps flex items-center gap-2">
          <Tags className="h-3.5 w-3.5" /> Tag it — up to three
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {tags.map((t) => (
            <button
              key={t.tag}
              type="button"
              data-testid={`forum-tag-pick-${t.tag}`}
              onClick={() => toggle(t.tag)}
              className={`border px-2 py-1 text-[11px] transition-colors duration-200 ${
                picked.includes(t.tag)
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
            >
              {t.tag}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex gap-2">
        <Button
          className="rounded-none"
          disabled={busy}
          data-testid="forum-composer-submit"
          onClick={submit}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Post it to the forum
        </Button>
        <Button variant="outline" className="rounded-none" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const ThreadRow = ({ thread, onOpen, onVote }) => (
  <div
    data-testid={`forum-row-${thread.id}`}
    onClick={() => onOpen(thread.id)}
    className="flex cursor-pointer items-start gap-4 bg-card p-4 transition-colors duration-200 hover:bg-muted/60"
  >
    <VoteArrows
      votes={thread.votes}
      my={thread.my_vote}
      testId={`vote-row-${thread.id}`}
      onVote={(v) => onVote(thread, v)}
    />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <KindBadge kind={thread.kind} />
        {thread.pinned && (
          <span className="inline-flex items-center gap-1 border border-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
            <Pin className="h-3 w-3" /> Pinned
          </span>
        )}
        {thread.locked && (
          <span className="inline-flex items-center gap-1 border border-[hsl(var(--watch))] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--watch))]">
            <Lock className="h-3 w-3" /> Locked
          </span>
        )}
        {thread.answered && (
          <span
            className="inline-flex items-center gap-1 border border-[hsl(var(--healthy))] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--healthy))]"
            data-testid={`answered-${thread.id}`}
          >
            <Check className="h-3 w-3" /> Answered
          </span>
        )}
      </div>
      <h3 className="mt-2 font-display text-base font-bold leading-snug">{thread.title}</h3>
      {thread.body && (
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{thread.body}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="font-semibold text-foreground">{thread.author}</span>
        {thread.author_plant && <span>· {thread.author_plant}</span>}
        <span>· asked {ago(thread.created_at)}</span>
        {thread.tags?.map((t) => (
          <span key={t} className="border border-border px-1.5 py-0.5">
            {t}
          </span>
        ))}
      </div>
    </div>
    <div className="shrink-0 text-right">
      <div className="font-data text-lg font-bold" data-testid={`replies-${thread.id}`}>
        {thread.replies}
      </div>
      <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
        {thread.kind === "question" ? "answers" : "replies"}
      </div>
      <div className="font-data mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        active {ago(thread.last_activity_at)}
      </div>
    </div>
  </div>
);

export default function Forum() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { shows, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [asking, setAsking] = useState(false);

  const sort = params.get("sort") || "active";
  const tag = params.get("tag") || "";
  const mine = params.get("mine") === "true";
  const unanswered = params.get("unanswered") === "true";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const load = useCallback(() => {
    api
      .get("/forum", {
        params: {
          sort,
          tag,
          q,
          mine: mine ? "true" : "",
          unanswered: unanswered ? "true" : "",
        },
      })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, tag, q, mine, unanswered]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const vote = (thread, value) =>
    api
      .post(`/forum/${thread.id}/vote`, { value })
      .then(load)
      .catch((e) => toast.error(apiError(e)));

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="forum-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (threadId)
    return (
      <div className="p-5 sm:p-8">
        <ForumThread
          threadId={threadId}
          canReply={shows("forum.reply")}
          canModerate={data.can_moderate && shows("forum.moderate")}
          onBack={() => navigate("/forum")}
          onChanged={load}
        />
      </div>
    );

  return (
    <div className="p-5 sm:p-8" data-testid="forum-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps flex items-center gap-2 text-primary">
            <MessagesSquare className="h-3.5 w-3.5" /> Discussion Forum
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Ask the units, not the inbox
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            One place for the questions that go round SAIL by phone — how a unit baselined a KPI, who
            got a sensor working, what a vendor actually delivered. Ask, answer, vote the useful
            answers up, and the officer who asked marks the one that settled it. Replies reach you on
            the bell in the header — no e-mail.
          </p>
        </div>
        {shows("forum.ask") && !asking && (
          <Button
            className="rounded-none"
            data-testid="forum-ask-button"
            onClick={() => setAsking(true)}
          >
            <Plus className="mr-2 h-4 w-4" /> Start a thread
          </Button>
        )}
      </div>

      {asking && (
        <div className="mt-6">
          <Composer
            tags={data.tags}
            onCancel={() => setAsking(false)}
            onDone={(id) => {
              setAsking(false);
              load();
              if (id) navigate(`/forum/${id}`);
            }}
          />
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[220px_1fr]">
        <aside data-testid="forum-tag-rail">
          <div className="label-caps flex items-center gap-2">
            <Tags className="h-3.5 w-3.5" /> Tags
          </div>
          <div className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-px">
            <button
              type="button"
              data-testid="forum-tag-all"
              onClick={() => setFilter("tag", "")}
              className={`border px-2 py-1.5 text-left text-xs transition-colors duration-200 lg:border-l-2 lg:border-r-0 lg:border-t-0 lg:border-b-0 ${
                !tag
                  ? "border-primary text-foreground lg:bg-muted"
                  : "border-border text-muted-foreground hover:text-foreground lg:border-transparent lg:hover:bg-muted"
              }`}
            >
              Every thread
              <span className="font-data float-right ml-3 text-muted-foreground">
                {data.threads.length}
              </span>
            </button>
            {data.tags.map((t) => (
              <button
                key={t.tag}
                type="button"
                data-testid={`forum-tag-${t.tag}`}
                onClick={() => setFilter("tag", tag === t.tag ? "" : t.tag)}
                className={`border px-2 py-1.5 text-left text-xs transition-colors duration-200 lg:border-l-2 lg:border-r-0 lg:border-t-0 lg:border-b-0 ${
                  tag === t.tag
                    ? "border-primary text-foreground lg:bg-muted"
                    : "border-border text-muted-foreground hover:text-foreground lg:border-transparent lg:hover:bg-muted"
                }`}
              >
                {t.tag}
                <span className="font-data float-right ml-3 text-muted-foreground">
                  {t.threads}
                </span>
              </button>
            ))}
          </div>
          {data.can_moderate && (
            <p className="mt-4 border border-border bg-muted p-3 text-[11px] text-muted-foreground">
              You can pin a thread to the top, lock it against further replies, or take a post down —
              open the thread and the controls are under it.
            </p>
          )}
        </aside>

        <div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-px border border-border bg-border">
              {SORTS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`forum-sort-${value}`}
                  onClick={() => setFilter("sort", value)}
                  className={`px-3 py-2 font-display text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                    sort === value
                      ? "bg-foreground text-background"
                      : "bg-card text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="forum-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the forum…"
                className="h-10 rounded-none bg-card pl-9"
              />
            </div>
            {[
              ["mine", "Mine", mine],
              ["unanswered", "Unanswered", unanswered],
            ].map(([key, label, on]) => (
              <button
                key={key}
                type="button"
                data-testid={`forum-filter-${key}`}
                onClick={() => setFilter(key, on ? "" : "true")}
                className={`border px-3 py-2 font-display text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                  on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div
            className="mt-4 grid gap-px border border-border bg-border"
            data-testid="forum-threads"
          >
            {data.threads.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                onOpen={(id) => navigate(`/forum/${id}`)}
                onVote={vote}
              />
            ))}
            {!data.threads.length && (
              <div className="bg-card p-10 text-center">
                <MessagesSquare className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-4 text-sm text-muted-foreground">
                  {tag || q || mine || unanswered
                    ? "Nothing on the forum matches that. Clear the slicers, or start the thread yourself."
                    : "The forum is empty. Be the first to ask — somebody in another unit has almost certainly solved it already."}
                </p>
                {shows("forum.ask") && (
                  <Button
                    className="mt-4 rounded-none"
                    data-testid="forum-ask-empty"
                    onClick={() => setAsking(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Start the first thread
                  </Button>
                )}
              </div>
            )}
          </div>

          {isAdmin && data.threads.length > 0 && (
            <p className="mt-3 text-[11px] text-muted-foreground">
              {data.threads.length} thread(s) on the forum · nothing here touches the register, so
              no post waits for an approval.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
